// src/api/productos.js - VERSIÓN COMPLETA PARA LA NUEVA API
// API Base: /api/Producto
// Imagen API: /api/Imagen

import { apiFetch, getCurrentApiUrl, getCurrentServer } from './index.js';
import { toTitleCase } from '@/utils/stringUtils.js';
import { obtenerTasaVentaActual } from './tasaVenta.js';
import { obtenerTasaCompraActual } from './tasaCompra.js';

// ==================== CONSTANTES ====================
const API_BASE = '/api/Producto';
const IMAGEN_API = '/api/Imagen';

// ==================== FUNCIONES PRINCIPALES ====================

/**
 * Obtener todos los productos
 * @param {string} estado - 'activos' (default), 'todos', 'inactivos'
 * @returns {Promise} Lista de productos
 */
export const obtenerProductos = async (estado = 'activos') => {
  try {
    console.log('📋 Obteniendo productos...', { estado });
    
    let endpoint = API_BASE;
    
    if (estado === 'activos') {
      endpoint = API_BASE;
    } else if (estado === 'inactivos') {
      endpoint = API_BASE;
    }
    
    const response = await apiFetch(endpoint);
    
    let productos = [];
    
    if (response.success && Array.isArray(response.data)) {
      productos = response.data;
    } else if (Array.isArray(response)) {
      productos = response;
    } else if (response.data && Array.isArray(response.data)) {
      productos = response.data;
    }
    
    if (estado === 'inactivos') {
      productos = productos.filter(p => p.estado?.toLowerCase() === 'inactivo');
    } else if (estado === 'activos') {
      productos = productos.filter(p => p.estado?.toLowerCase() === 'activo');
    }
    
    return {
      success: true,
      data: productos,
      total: productos.length,
      count: productos.length,
      message: 'Productos obtenidos exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error obteniendo productos:', error);
    return {
      success: false,
      error: error.message,
      data: [],
      total: 0,
      count: 0
    };
  }
};

/**
 * Obtener un producto por ID
 * @param {number} id - ID del producto
 * @returns {Promise} Datos del producto
 */
export const obtenerProductoPorId = async (id) => {
  try {
    if (!id) throw new Error('ID de producto es requerido');
    
    console.log(`🔍 Obteniendo producto ID: ${id}`);
    
    const response = await apiFetch(`${API_BASE}/${id}`);
    
    const producto = response.success ? response.data : response;
    
    return {
      success: true,
      data: producto,
      message: response.message || 'Producto obtenido exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo producto ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Obtener producto por código
 * @param {string} codigo - Código del producto
 * @returns {Promise} Datos del producto
 */
export const obtenerProductoPorCodigo = async (codigo) => {
  try {
    if (!codigo) throw new Error('Código de producto es requerido');
    
    console.log(`🔍 Obteniendo producto por código: ${codigo}`);
    
    const response = await apiFetch(`${API_BASE}/codigo/${encodeURIComponent(codigo)}`);
    
    const producto = response.success ? response.data : response;
    
    return {
      success: true,
      data: producto,
      message: response.message || 'Producto obtenido exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo producto por código ${codigo}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

// ==================== NUEVO: OBTENER POR CÓDIGO DE BARRAS ====================

/**
 * Obtener producto por código de barras
 * @param {string} codigoBarras - Código de barras del producto
 * @returns {Promise} Datos del producto
 */
export const obtenerProductoPorCodigoBarras = async (codigoBarras) => {
  try {
    if (!codigoBarras) throw new Error('Código de barras es requerido');
    
    console.log(`🔍 Obteniendo producto por código de barras: ${codigoBarras}`);
    
    const response = await apiFetch(`${API_BASE}/codigo-barras/${encodeURIComponent(codigoBarras)}`);
    
    const producto = response.success ? response.data : response;
    
    return {
      success: true,
      data: producto,
      message: response.message || 'Producto obtenido exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo producto por código de barras ${codigoBarras}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Buscar productos avanzado
 * @param {Object} criterios - Criterios de búsqueda
 * @returns {Promise} Productos encontrados
 */
export const buscarProductosAvanzado = async (criterios = {}) => {
  try {
    console.log('🔎 Buscando productos con criterios:', criterios);
    
    const response = await apiFetch(`${API_BASE}/search`, {
      method: 'POST',
      body: JSON.stringify(criterios)
    });
    
    const productos = response.success ? response.data : (Array.isArray(response) ? response : []);
    
    return {
      success: true,
      data: productos,
      count: productos.length,
      message: response.message || 'Búsqueda completada'
    };
    
  } catch (error) {
    console.error('❌ Error en búsqueda avanzada:', error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Crear un nuevo producto
 * @param {Object} productoData - Datos del producto (en snake_case)
 * @returns {Promise} Producto creado
 */
export const crearProducto = async (productoData) => {
  try {
    if (!productoData.codigo?.trim()) throw new Error('El código es requerido');
    if (!productoData.nombre?.trim()) throw new Error('El nombre es requerido');
    if (!productoData.precio_compra_usdt || productoData.precio_compra_usdt <= 0) {
      throw new Error('El precio de compra debe ser mayor a 0');
    }
    if (!productoData.idCategoria) throw new Error('La categoría es requerida');
    if (!productoData.idProveedor) throw new Error('El proveedor es requerido');
    
    console.log('➕ Creando nuevo producto:', productoData);
    
    const datosEnviar = {
      codigo: productoData.codigo.trim(),
      codigoBarras: productoData.codigoBarras?.trim() || null,  // <-- NUEVO
      nombre: toTitleCase(productoData.nombre),
      descripcion: productoData.descripcion?.trim() || null,
      marca: toTitleCase(productoData.marca || ''),
      modelo: toTitleCase(productoData.modelo || ''),
      precio_compra_usdt: Number(productoData.precio_compra_usdt),
      tipo_ganancia: productoData.tipo_ganancia || 'PORCENTAJE',
      valor_ganancia: productoData.valor_ganancia || 35,
      precio_venta_usd: Number(productoData.precio_venta_usd) || null,
      precio_venta_bs: Number(productoData.precio_venta_bs) || null,
      precio_especial_usd: productoData.precio_especial_usd ? Number(productoData.precio_especial_usd) : null,
      precio_especial_bs: productoData.precio_especial_bs ? Number(productoData.precio_especial_bs) : null,
      stock: productoData.stock || 0,
      stock_minimo: productoData.stock_minimo || 5,
      stockMaximo: productoData.stockMaximo || null,
      garantia_meses: productoData.garantia_meses || 0,
      garantia_descripcion: productoData.garantia_descripcion || null,
      imagen: productoData.imagen || null,
      ubicacion: productoData.ubicacion || null,
      estado: productoData.estado || 'Activo',
      idCategoria: Number(productoData.idCategoria),
      idProveedor: Number(productoData.idProveedor)
    };
    
    const response = await apiFetch(API_BASE, {
      method: 'POST',
      body: JSON.stringify(datosEnviar)
    });
    
    if (!response.success) {
      throw new Error(response.message || 'Error al crear producto');
    }
    
    return {
      success: true,
      data: response.data,
      message: response.message || 'Producto creado exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error creando producto:', error);
    return { success: false, error: error.message, data: null };
  }
};

/**
 * Actualizar un producto existente
 * @param {number} id - ID del producto
 * @param {Object} productoData - Datos a actualizar (en snake_case)
 * @returns {Promise} Producto actualizado
 */
export const actualizarProducto = async (id, productoData) => {
  try {
    if (!id) throw new Error('ID de producto es requerido');
    
    console.log(`✏️ Actualizando producto ID: ${id}`, productoData);
    
    const datosEnviar = {
      idProducto: id,
      codigo: productoData.codigo?.trim(),
      codigoBarras: productoData.codigoBarras?.trim() || null,  // <-- NUEVO
      nombre: productoData.nombre ? toTitleCase(productoData.nombre) : undefined,
      descripcion: productoData.descripcion?.trim(),
      marca: productoData.marca ? toTitleCase(productoData.marca) : undefined,
      modelo: productoData.modelo ? toTitleCase(productoData.modelo) : undefined,
      precio_compra_usdt: productoData.precio_compra_usdt !== undefined ? Number(productoData.precio_compra_usdt) : undefined,
      tipo_ganancia: productoData.tipo_ganancia,
      valor_ganancia: productoData.valor_ganancia,
      precio_venta_usd: productoData.precio_venta_usd !== undefined ? Number(productoData.precio_venta_usd) : undefined,
      precio_venta_bs: productoData.precio_venta_bs !== undefined ? Number(productoData.precio_venta_bs) : undefined,
      precio_especial_usd: productoData.precio_especial_usd !== undefined 
        ? (productoData.precio_especial_usd ? Number(productoData.precio_especial_usd) : null) 
        : undefined,
      precio_especial_bs: productoData.precio_especial_bs !== undefined 
        ? (productoData.precio_especial_bs ? Number(productoData.precio_especial_bs) : null) 
        : undefined,
      stock: productoData.stock,
      stock_minimo: productoData.stock_minimo,
      stockMaximo: productoData.stockMaximo,
      garantia_meses: productoData.garantia_meses,
      garantia_descripcion: productoData.garantia_descripcion,
      imagen: productoData.imagen,
      ubicacion: productoData.ubicacion,
      estado: productoData.estado,
      idCategoria: productoData.idCategoria ? Number(productoData.idCategoria) : undefined,
      idProveedor: productoData.idProveedor ? Number(productoData.idProveedor) : undefined,
      EliminarImagen: productoData.EliminarImagen || false
    };
    
    Object.keys(datosEnviar).forEach(key => 
      datosEnviar[key] === undefined && delete datosEnviar[key]
    );
    
    const response = await apiFetch(`${API_BASE}/${id}`, {
      method: 'PUT',
      body: JSON.stringify(datosEnviar)
    });
    
    if (!response.success) {
      throw new Error(response.message || 'Error al actualizar producto');
    }
    
    return {
      success: true,
      data: response.data,
      message: response.message || 'Producto actualizado exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error actualizando producto ${id}:`, error);
    return { success: false, error: error.message, data: null };
  }
};

/**
 * Eliminar un producto (cambia estado a inactivo si tiene movimientos)
 * @param {number} id - ID del producto a eliminar
 * @returns {Promise} Resultado de la eliminación
 */
export const eliminarProducto = async (id) => {
  try {
    if (!id) throw new Error('ID de producto es requerido');
    
    console.log(`🗑️ Eliminando producto ID: ${id}`);
    
    const response = await apiFetch(`${API_BASE}/${id}`, { 
      method: 'DELETE' 
    });
    
    return {
      success: true,
      data: response.data || response,
      message: response.message || 'Producto eliminado exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error eliminando producto ${id}:`, error);
    return { success: false, error: error.message, data: null };
  }
};

/**
 * Cambiar estado de un producto
 * @param {number} id - ID del producto
 * @param {string} estado - Nuevo estado ('Activo' o 'Inactivo')
 * @returns {Promise} Resultado del cambio de estado
 */
export const cambiarEstadoProducto = async (id, estado) => {
  try {
    if (!id) throw new Error('ID es requerido');
    if (!estado) throw new Error('Estado es requerido');
    
    console.log(`🔄 Cambiando estado de producto ID: ${id} a ${estado}`);
    
    const userStr = localStorage.getItem('user') || localStorage.getItem('user_data');
    const userData = userStr ? JSON.parse(userStr) : null;
    const idUsuarioActual = userData ? (userData.idUsuario || userData.id || 1) : 1;

    const response = await apiFetch(`${API_BASE}/${id}/estado`, {
      method: 'PATCH',
      body: JSON.stringify({ 
        estado,
        idUsuario: idUsuarioActual
      })
    });
    
    return {
      success: true,
      data: response.data || response,
      message: response.message || 'Estado actualizado exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error cambiando estado de producto ${id}:`, error);
    return { success: false, error: error.message, data: null };
  }
};

/**
 * Ajustar stock de un producto
 * @param {Object} ajusteDto - Datos del ajuste
 * @returns {Promise} Resultado del ajuste
 */
export const ajustarStock = async (ajusteDto) => {
  try {
    if (!ajusteDto.idProducto) throw new Error('ID de producto es requerido');
    if (!ajusteDto.cantidad || ajusteDto.cantidad === 0) throw new Error('Cantidad válida es requerida');
    
    console.log(`📦 Ajustando stock producto ID: ${ajusteDto.idProducto}`, ajusteDto);
    
    const userStr = localStorage.getItem('user') || localStorage.getItem('user_data');
    const userData = userStr ? JSON.parse(userStr) : null;
    const idUsuarioActual = userData ? (userData.idUsuario || userData.id || 1) : 1;

    const response = await apiFetch(`${API_BASE}/ajustar-stock`, {
      method: 'POST',
      body: JSON.stringify({
        idProducto: ajusteDto.idProducto,
        cantidad: Number(ajusteDto.cantidad),
        motivo: ajusteDto.motivo || 'Ajuste manual',
        observaciones: ajusteDto.observaciones || null,
        idUsuario: ajusteDto.idUsuario || idUsuarioActual
      })
    });
    
    return {
      success: true,
      data: response.data || response,
      message: response.message || 'Stock ajustado exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error ajustando stock:`, error);
    return { success: false, error: error.message, data: null };
  }
};

/**
 * Actualizar stock (wrapper para mantener compatibilidad)
 * @param {number} id - ID del producto
 * @param {number} cantidad - Cantidad a ajustar (positiva para aumentar, negativa para disminuir)
 * @param {string} operacion - 'incrementar', 'disminuir' (se ignora, se usa el signo de cantidad)
 * @returns {Promise} Resultado de la actualización
 */
export const actualizarStock = async (id, cantidad, operacion = 'incrementar') => {
  try {
    if (!id) throw new Error('ID es requerido');
    if (!cantidad || cantidad <= 0) throw new Error('Cantidad inválida');
    
    let cantidadAjuste = Number(cantidad);
    if (operacion === 'disminuir') {
      cantidadAjuste = -cantidadAjuste;
    }
    
    console.log(`📦 Actualizando stock producto ID: ${id} - ${operacion} ${cantidad}`);
    
    const userStr = localStorage.getItem('user') || localStorage.getItem('user_data');
    const userData = userStr ? JSON.parse(userStr) : null;
    const idUsuarioActual = userData ? (userData.idUsuario || userData.id || 1) : 1;

    return await ajustarStock({
      idProducto: id,
      cantidad: cantidadAjuste,
      motivo: `Actualización por ${operacion}`,
      observaciones: `Operación: ${operacion}`,
      idUsuario: idUsuarioActual
    });
    
  } catch (error) {
    console.error(`❌ Error actualizando stock producto ${id}:`, error);
    return { success: false, error: error.message, data: null };
  }
};

/**
 * Incrementar stock
 * @param {number} id - ID del producto
 * @param {number} cantidad - Cantidad a incrementar
 * @returns {Promise} Resultado
 */
export const incrementarStock = async (id, cantidad) => {
  return await actualizarStock(id, cantidad, 'incrementar');
};

/**
 * Disminuir stock
 * @param {number} id - ID del producto
 * @param {number} cantidad - Cantidad a disminuir
 * @returns {Promise} Resultado
 */
export const disminuirStock = async (id, cantidad) => {
  return await actualizarStock(id, cantidad, 'disminuir');
};

// ==================== FUNCIONES DE CONSULTA ESPECÍFICAS ====================

/**
 * Obtener productos por categoría
 * @param {number} idCategoria - ID de la categoría
 * @returns {Promise} Productos de la categoría
 */
export const obtenerProductosPorCategoria = async (idCategoria) => {
  try {
    if (!idCategoria) throw new Error('ID de categoría es requerido');
    
    console.log(`📂 Obteniendo productos de categoría ID: ${idCategoria}`);
    
    const response = await apiFetch(`${API_BASE}/categoria/${idCategoria}`);
    
    const productos = response.success ? response.data : (Array.isArray(response) ? response : []);
    
    return {
      success: true,
      data: productos,
      count: productos.length,
      message: 'Productos por categoría obtenidos'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo productos por categoría ${idCategoria}:`, error);
    return { success: false, error: error.message, data: [], count: 0 };
  }
};

/**
 * Obtener productos por proveedor
 * @param {number} idProveedor - ID del proveedor
 * @returns {Promise} Productos del proveedor
 */
export const obtenerProductosPorProveedor = async (idProveedor) => {
  try {
    if (!idProveedor) throw new Error('ID de proveedor es requerido');
    
    console.log(`🏢 Obteniendo productos de proveedor ID: ${idProveedor}`);
    
    const response = await apiFetch(`${API_BASE}/proveedor/${idProveedor}`);
    
    const productos = response.success ? response.data : (Array.isArray(response) ? response : []);
    
    return {
      success: true,
      data: productos,
      count: productos.length,
      message: 'Productos por proveedor obtenidos'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo productos por proveedor ${idProveedor}:`, error);
    return { success: false, error: error.message, data: [], count: 0 };
  }
};

/**
 * Obtener productos bajo stock
 * @returns {Promise} Productos bajo stock
 */
export const obtenerProductosBajoStock = async () => {
  try {
    console.log('📉 Obteniendo productos bajo stock');
    
    const response = await apiFetch(`${API_BASE}/stock-bajo`);
    
    const productos = response.success ? response.data : (Array.isArray(response) ? response : []);
    
    return {
      success: true,
      data: productos,
      count: productos.length,
      message: response.message || 'Productos bajo stock obtenidos'
    };
    
  } catch (error) {
    console.error('❌ Error obteniendo productos bajo stock:', error);
    return { success: false, error: error.message, data: [], count: 0 };
  }
};

/**
 * Obtener productos sin stock
 * @returns {Promise} Productos sin stock
 */
export const obtenerProductosSinStock = async () => {
  try {
    console.log('📦 Obteniendo productos sin stock');
    
    const response = await apiFetch(`${API_BASE}/sin-stock`);
    
    const productos = response.success ? response.data : (Array.isArray(response) ? response : []);
    
    return {
      success: true,
      data: productos,
      count: productos.length,
      message: response.message || 'Productos sin stock obtenidos'
    };
    
  } catch (error) {
    console.error('❌ Error obteniendo productos sin stock:', error);
    return { success: false, error: error.message, data: [], count: 0 };
  }
};

/**
 * Obtener stock total
 * @returns {Promise} Stock total
 */
export const obtenerStockTotal = async () => {
  try {
    console.log('📊 Obteniendo stock total');
    
    const response = await apiFetch(`${API_BASE}/stock-total`);
    
    return {
      success: true,
      data: response,
      total: response,
      message: 'Stock total obtenido'
    };
    
  } catch (error) {
    console.error('❌ Error obteniendo stock total:', error);
    return { success: false, error: error.message, data: 0 };
  }
};

/**
 * Obtener valor del inventario
 * @returns {Promise} Valor del inventario
 */
export const obtenerValorInventario = async () => {
  try {
    console.log('💰 Obteniendo valor del inventario');
    
    const response = await apiFetch(`${API_BASE}/valor-inventario`);
    
    return {
      success: true,
      data: response,
      valor: response,
      message: 'Valor del inventario obtenido'
    };
    
  } catch (error) {
    console.error('❌ Error obteniendo valor del inventario:', error);
    return { success: false, error: error.message, data: 0 };
  }
};

// ==================== FUNCIONES DE CÁLCULO DE PRECIOS ====================

/**
 * Calcular precio de venta para un producto
 * @param {number} id - ID del producto
 * @returns {Promise} Cálculo de precio
 */
export const calcularPrecioVenta = async (id) => {
  try {
    if (!id) throw new Error('ID de producto es requerido');
    
    console.log(`🧮 Calculando precio de venta para producto ID: ${id}`);
    
    const response = await apiFetch(`${API_BASE}/${id}/calcular-precio`);
    
    return {
      success: true,
      data: response.data || response,
      message: response.message || 'Precio calculado exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error calculando precio producto ${id}:`, error);
    return { success: false, error: error.message, data: null };
  }
};

/**
 * Calcular precio con margen personalizado
 * @param {number} productoId - ID del producto
 * @param {number} margenPersonalizado - Margen personalizado
 * @returns {Promise} Precio calculado
 */
export const calcularPrecioPersonalizado = async (productoId, margenPersonalizado) => {
  try {
    if (!productoId) throw new Error('ID de producto es requerido');
    if (!margenPersonalizado || margenPersonalizado <= 0) throw new Error('Margen válido es requerido');
    
    console.log(`🧮 Calculando precio personalizado para producto ID: ${productoId} con margen ${margenPersonalizado}%`);
    
    const response = await apiFetch(`${API_BASE}/calcular-precio-personalizado`, {
      method: 'POST',
      body: JSON.stringify({
        productoId,
        margenPersonalizado: Number(margenPersonalizado)
      })
    });
    
    return {
      success: true,
      data: response.data || response,
      message: response.message || 'Precio personalizado calculado'
    };
    
  } catch (error) {
    console.error(`❌ Error calculando precio personalizado:`, error);
    return { success: false, error: error.message, data: null };
  }
};

/**
 * Cálculo rápido de precio
 * @param {number} costoUSD - Costo en USD
 * @param {number} margenPorcentaje - Margen porcentual
 * @returns {Promise} Precio calculado
 */
export const calcularPrecioRapido = async (costoUSD, margenPorcentaje) => {
  try {
    if (!costoUSD || costoUSD <= 0) throw new Error('Costo válido es requerido');
    if (!margenPorcentaje || margenPorcentaje <= 0) throw new Error('Margen válido es requerido');
    
    console.log(`⚡ Cálculo rápido: $${costoUSD} con ${margenPorcentaje}% de margen`);
    
    const response = await apiFetch(`${API_BASE}/calcular-rapido`, {
      method: 'POST',
      body: JSON.stringify({
        costoUSD: Number(costoUSD),
        margenPorcentaje: Number(margenPorcentaje)
      })
    });
    
    return {
      success: true,
      data: response.data || response,
      message: response.message || 'Cálculo rápido completado'
    };
    
  } catch (error) {
    console.error(`❌ Error en cálculo rápido:`, error);
    return { success: false, error: error.message, data: null };
  }
};

/**
 * Actualizar precio de un producto
 * @param {number} id - ID del producto
 * @param {number} nuevoPrecioUSD - Nuevo precio en USD
 * @param {number} nuevoPrecioBS - Nuevo precio en Bs
 * @param {number} idUsuario - ID del usuario que realiza la actualización
 * @returns {Promise} Resultado de la actualización
 */
export const actualizarPrecioProducto = async (id, nuevoPrecioUSD, nuevoPrecioBS, idUsuario) => {
  try {
    if (!id) throw new Error('ID de producto es requerido');
    if (!nuevoPrecioUSD || nuevoPrecioUSD <= 0) throw new Error('Precio USD válido es requerido');
    if (!nuevoPrecioBS || nuevoPrecioBS <= 0) throw new Error('Precio BS válido es requerido');
    
    console.log(`💰 Actualizando precio producto ID: ${id} a $${nuevoPrecioUSD} / Bs.${nuevoPrecioBS}`);
    
    const response = await apiFetch(`${API_BASE}/${id}/actualizar-precio`, {
      method: 'PUT',
      body: JSON.stringify({
        nuevoPrecioUSD: Number(nuevoPrecioUSD),
        nuevoPrecioBS: Number(nuevoPrecioBS),
        idUsuario: idUsuario || null
      })
    });
    
    return {
      success: true,
      data: response.data || response,
      message: response.message || 'Precio actualizado exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error actualizando precio producto ${id}:`, error);
    return { success: false, error: error.message, data: null };
  }
};

/**
 * Actualizar precios en lote
 * @param {Object} loteDto - Datos para la actualización en lote
 * @returns {Promise} Resultado de la actualización
 */
export const actualizarPreciosEnLote = async (loteDto) => {
  try {
    if (!loteDto.ProductoIds || !loteDto.ProductoIds.length) {
      throw new Error('Lista de productos es requerida');
    }
    if (!loteDto.Valor || loteDto.Valor <= 0) throw new Error('Valor válido es requerido');
    
    console.log(`📦 Actualizando precios en lote para ${loteDto.ProductoIds.length} productos`);
    
    const response = await apiFetch(`${API_BASE}/actualizar-precios-lote`, {
      method: 'POST',
      body: JSON.stringify({
        ProductoIds: loteDto.ProductoIds,
        TipoActualizacion: loteDto.TipoActualizacion || 'PORCENTAJE',
        Valor: Number(loteDto.Valor),
        AplicarAPrecioUSD: loteDto.AplicarAPrecioUSD !== false,
        AplicarAPrecioBS: loteDto.AplicarAPrecioBS !== false
      })
    });
    
    return {
      success: true,
      data: response.data || response,
      message: response.message || 'Actualización en lote completada'
    };
    
  } catch (error) {
    console.error(`❌ Error en actualización en lote:`, error);
    return { success: false, error: error.message, data: null };
  }
};

// ==================== ESTADÍSTICAS Y DASHBOARD ====================

/**
 * Obtener estadísticas de un producto
 * @param {number} id - ID del producto
 * @returns {Promise} Estadísticas del producto
 */
export const obtenerEstadisticasProducto = async (id) => {
  try {
    if (!id) throw new Error('ID de producto es requerido');
    
    console.log(`📊 Obteniendo estadísticas del producto ID: ${id}`);
    
    const response = await apiFetch(`${API_BASE}/${id}/estadisticas`);
    
    return {
      success: true,
      data: response.data || response,
      message: response.message || 'Estadísticas obtenidas'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo estadísticas producto ${id}:`, error);
    return { success: false, error: error.message, data: null };
  }
};

/**
 * Obtener dashboard de productos
 * @returns {Promise} Datos del dashboard
 */
export const obtenerDashboardProductos = async () => {
  try {
    console.log('📊 Obteniendo dashboard de productos');
    
    const response = await apiFetch(`${API_BASE}/dashboard`);
    
    return {
      success: true,
      data: response.data || response,
      message: response.message || 'Dashboard obtenido'
    };
    
  } catch (error) {
    console.error('❌ Error obteniendo dashboard:', error);
    return { success: false, error: error.message, data: null };
  }
};

/**
 * Obtener productos más vendidos
 * @param {number} cantidad - Cantidad de productos a obtener
 * @returns {Promise} Productos más vendidos
 */
export const obtenerProductosMasVendidos = async (cantidad = 10) => {
  try {
    console.log(`🏆 Obteniendo ${cantidad} productos más vendidos`);
    
    const response = await apiFetch(`${API_BASE}/mas-vendidos?cantidad=${cantidad}`);
    
    const productos = response.success ? response.data : (Array.isArray(response) ? response : []);
    
    return {
      success: true,
      data: productos,
      count: productos.length,
      message: response.message || 'Productos más vendidos obtenidos'
    };
    
  } catch (error) {
    console.error('❌ Error obteniendo productos más vendidos:', error);
    return { success: false, error: error.message, data: [], count: 0 };
  }
};

/**
 * Obtener productos menos vendidos
 * @param {number} cantidad - Cantidad de productos a obtener
 * @returns {Promise} Productos menos vendidos
 */
export const obtenerProductosMenosVendidos = async (cantidad = 10) => {
  try {
    console.log(`📉 Obteniendo ${cantidad} productos menos vendidos`);
    
    const response = await apiFetch(`${API_BASE}/menos-vendidos?cantidad=${cantidad}`);
    
    const productos = response.success ? response.data : (Array.isArray(response) ? response : []);
    
    return {
      success: true,
      data: productos,
      count: productos.length,
      message: response.message || 'Productos menos vendidos obtenidos'
    };
    
  } catch (error) {
    console.error('❌ Error obteniendo productos menos vendidos:', error);
    return { success: false, error: error.message, data: [], count: 0 };
  }
};

// ==================== HISTORIAL Y MOVIMIENTOS ====================

/**
 * Obtener historial de precios de un producto
 * @param {number} id - ID del producto
 * @param {string} fechaInicio - Fecha de inicio (opcional)
 * @param {string} fechaFin - Fecha de fin (opcional)
 * @returns {Promise} Historial de precios
 */
export const obtenerHistorialPrecios = async (id, fechaInicio = null, fechaFin = null) => {
  try {
    if (!id) throw new Error('ID de producto es requerido');
    
    let url = `${API_BASE}/${id}/historial-precios`;
    const params = new URLSearchParams();
    
    if (fechaInicio) params.append('fechaInicio', fechaInicio);
    if (fechaFin) params.append('fechaFin', fechaFin);
    
    if (params.toString()) url += `?${params.toString()}`;
    
    console.log(`📜 Obteniendo historial de precios producto ID: ${id}`);
    
    const response = await apiFetch(url);
    
    const historial = response.success ? response.data : (Array.isArray(response) ? response : []);
    
    return {
      success: true,
      data: historial,
      count: historial.length,
      message: response.message || 'Historial de precios obtenido'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo historial de precios ${id}:`, error);
    return { success: false, error: error.message, data: [], count: 0 };
  }
};

/**
 * Obtener movimientos de stock de un producto
 * @param {number} id - ID del producto
 * @param {string} fechaInicio - Fecha de inicio (opcional)
 * @param {string} fechaFin - Fecha de fin (opcional)
 * @returns {Promise} Movimientos de stock
 */
export const obtenerMovimientosStock = async (id, fechaInicio = null, fechaFin = null) => {
  try {
    if (!id) throw new Error('ID de producto es requerido');
    
    let url = `${API_BASE}/${id}/movimientos-stock`;
    const params = new URLSearchParams();
    
    if (fechaInicio) params.append('fechaInicio', fechaInicio);
    if (fechaFin) params.append('fechaFin', fechaFin);
    
    if (params.toString()) url += `?${params.toString()}`;
    
    console.log(`📦 Obteniendo movimientos de stock producto ID: ${id}`);
    
    const response = await apiFetch(url);
    
    const movimientos = response.success ? response.data : (Array.isArray(response) ? response : []);
    
    return {
      success: true,
      data: movimientos,
      count: movimientos.length,
      message: response.message || 'Movimientos de stock obtenidos'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo movimientos de stock ${id}:`, error);
    return { success: false, error: error.message, data: [], count: 0 };
  }
};

// ==================== VALIDACIONES ====================

/**
 * Verificar si un código existe
 * @param {string} codigo - Código a verificar
 * @param {number} idProductoExcluir - ID del producto a excluir (para edición)
 * @returns {Promise} Resultado de verificación
 */
export const verificarCodigo = async (codigo, idProductoExcluir = null) => {
  try {
    if (!codigo?.trim()) throw new Error('Código es requerido');
    
    console.log(`🔍 Verificando código: ${codigo}`);
    
    let url = `${API_BASE}/validar-codigo?codigo=${encodeURIComponent(codigo)}`;
    if (idProductoExcluir) {
      url += `&idProductoExcluir=${idProductoExcluir}`;
    }
    
    const response = await apiFetch(url);
    
    return {
      success: true,
      data: response,
      existe: response?.existe || false,
      message: response?.message || 'Verificación completada'
    };
    
  } catch (error) {
    console.error(`❌ Error verificando código ${codigo}:`, error);
    return { success: false, error: error.message, existe: false };
  }
};

// ==================== NUEVO: VALIDAR CÓDIGO DE BARRAS ====================

/**
 * Verificar si un código de barras existe
 * @param {string} codigoBarras - Código de barras a verificar
 * @param {number} idProductoExcluir - ID del producto a excluir (para edición)
 * @returns {Promise} Resultado de verificación
 */
export const verificarCodigoBarras = async (codigoBarras, idProductoExcluir = null) => {
  try {
    if (!codigoBarras?.trim()) {
      // Si no hay código de barras, no es obligatorio
      return { success: true, existe: false, data: { existe: false } };
    }
    
    console.log(`🔍 Verificando código de barras: ${codigoBarras}`);
    
    let url = `${API_BASE}/validar-codigo-barras?codigoBarras=${encodeURIComponent(codigoBarras)}`;
    if (idProductoExcluir) {
      url += `&idProductoExcluir=${idProductoExcluir}`;
    }
    
    const response = await apiFetch(url);
    
    return {
      success: true,
      data: response,
      existe: response?.existe || false,
      message: response?.message || 'Verificación completada'
    };
    
  } catch (error) {
    console.error(`❌ Error verificando código de barras ${codigoBarras}:`, error);
    return { success: false, error: error.message, existe: false };
  }
};

/**
 * Verificar si un producto tiene movimientos
 * @param {number} id - ID del producto
 * @returns {Promise} Si tiene movimientos
 */
export const verificarProductoTieneMovimientos = async (id) => {
  try {
    if (!id) throw new Error('ID de producto es requerido');
    
    console.log(`🔎 Verificando si producto ID: ${id} tiene movimientos`);
    
    const response = await apiFetch(`${API_BASE}/${id}/tiene-movimientos`);
    
    return {
      success: true,
      tieneMovimientos: response?.tieneMovimientos || false,
      data: response,
      message: response?.message || 'Verificación completada'
    };
    
  } catch (error) {
    console.error(`❌ Error verificando movimientos ${id}:`, error);
    return { success: false, error: error.message, tieneMovimientos: false };
  }
};

// ==================== FUNCIONES DE IMAGEN ====================

/**
 * Subir imagen de producto
 * @param {File} imagen - Archivo de imagen
 * @param {number} productoId - ID del producto (opcional)
 * @returns {Promise} Resultado de la subida
 */
export const subirImagenProducto = async (imagen, productoId = null) => {
  try {
    if (!imagen) throw new Error('Imagen es requerida');
    
    console.log('📤 Subiendo imagen de producto', { productoId });
    
    const currentServer = getCurrentServer();
    if (!currentServer) throw new Error('No hay servidor configurado');
    
    const url = `${currentServer.baseUrl.replace(':8080', ':5090')}${IMAGEN_API}/subir`;
    
    const formData = new FormData();
    formData.append('archivo', imagen);
    
    if (productoId) {
      formData.append('entidadId', productoId);
      formData.append('tipoEntidad', 'producto');
    }
    
    formData.append('descripcion', `Imagen de producto ${productoId || 'nuevo'}`);
    
    const response = await fetch(url, { 
      method: 'POST', 
      body: formData 
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.mensaje || data.error || 'Error al subir la imagen');
    }
    
    if (data.exito) {
      console.log('✅ Imagen subida exitosamente:', data);
      return {
        success: true,
        data: {
          nombreArchivo: data.nombreArchivo,
          rutaRelativa: data.rutaRelativa,
          url: data.url,
          urlCompleta: data.urlCompleta,
          mensaje: data.mensaje
        },
        message: data.mensaje || 'Imagen subida exitosamente'
      };
    } else {
      throw new Error(data.mensaje || 'Error al subir la imagen');
    }
    
  } catch (error) {
    console.error('❌ Error subiendo imagen:', error);
    return { success: false, error: error.message, data: null };
  }
};

/**
 * Obtener URL de imagen desde la API
 * @param {string} tipoEntidad - Tipo de entidad ('producto')
 * @param {number} entidadId - ID de la entidad
 * @returns {Promise<string|null>} URL de la imagen
 */
export const obtenerUrlImagenDesdeApi = async (tipoEntidad = 'producto', entidadId) => {
  try {
    if (!entidadId) return null;
    
    const response = await apiFetch(`${IMAGEN_API}/${tipoEntidad}/${entidadId}`);
    
    if (response.success && response.data && response.data.urlCompleta) {
      return response.data.urlCompleta;
    }
    
    return null;
    
  } catch (error) {
    console.error('❌ Error obteniendo URL de imagen:', error);
    return null;
  }
};

/**
 * Eliminar imagen de producto
 * @param {string} tipoEntidad - Tipo de entidad ('producto')
 * @param {number} entidadId - ID de la entidad
 * @returns {Promise} Resultado de la eliminación
 */
export const eliminarImagenProducto = async (tipoEntidad = 'producto', entidadId) => {
  try {
    if (!entidadId) throw new Error('ID de entidad es requerido');
    
    console.log(`🗑️ Eliminando imagen de ${tipoEntidad} ID: ${entidadId}`);
    
    const response = await apiFetch(`${IMAGEN_API}/${tipoEntidad}/${entidadId}`, {
      method: 'DELETE'
    });
    
    return {
      success: true,
      data: response.data || response,
      message: response.mensaje || 'Imagen eliminada exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error eliminando imagen:', error);
    return { success: false, error: error.message, data: null };
  }
};

/**
 * Validar imagen
 * @param {File} imagen - Archivo de imagen
 * @returns {Promise} Resultado de la validación
 */
export const validarImagen = async (imagen) => {
  try {
    if (!imagen) throw new Error('Imagen es requerida');
    
    const formData = new FormData();
    formData.append('imagen', imagen);
    
    const currentServer = getCurrentServer();
    const url = `${currentServer.baseUrl.replace(':8080', ':5090')}${IMAGEN_API}/validar`;
    
    const response = await fetch(url, { 
      method: 'POST', 
      body: formData 
    });
    
    const data = await response.json();
    
    return {
      success: true,
      valida: true,
      data: data,
      message: data.mensaje || 'Imagen válida'
    };
    
  } catch (error) {
    console.error('❌ Error validando imagen:', error);
    return { success: false, error: error.message, valida: false };
  }
};

/**
 * Descargar imagen
 * @param {string} tipoEntidad - Tipo de entidad ('producto')
 * @param {number} entidadId - ID de la entidad
 * @returns {Promise} Blob de la imagen
 */
export const descargarImagen = async (tipoEntidad = 'producto', entidadId) => {
  try {
    if (!entidadId) throw new Error('ID de entidad es requerido');
    
    const currentServer = getCurrentServer();
    const url = `${currentServer.baseUrl.replace(':8080', ':5090')}${IMAGEN_API}/descargar/${tipoEntidad}/${entidadId}`;
    
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error('Error al descargar la imagen');
    }
    
    return await response.blob();
    
  } catch (error) {
    console.error('❌ Error descargando imagen:', error);
    return null;
  }
};

/**
 * Obtener imagen en base64
 * @param {string} tipoEntidad - Tipo de entidad ('producto')
 * @param {number} entidadId - ID de la entidad
 * @returns {Promise<string|null>} Imagen en base64
 */
export const obtenerImagenBase64 = async (tipoEntidad = 'producto', entidadId) => {
  try {
    if (!entidadId) return null;
    
    const response = await apiFetch(`${IMAGEN_API}/base64/${tipoEntidad}/${entidadId}`);
    
    if (response.success && response.data && response.data.base64) {
      return response.data.base64;
    }
    
    return null;
    
  } catch (error) {
    console.error('❌ Error obteniendo imagen base64:', error);
    return null;
  }
};

/**
 * Verificar si una imagen existe
 * @param {string} tipoEntidad - Tipo de entidad ('producto')
 * @param {number} entidadId - ID de la entidad
 * @returns {Promise<boolean>} Si la imagen existe
 */
export const verificarImagenExiste = async (tipoEntidad = 'producto', entidadId) => {
  try {
    if (!entidadId) return false;
    
    const response = await apiFetch(`${IMAGEN_API}/existe/${tipoEntidad}/${entidadId}`);
    
    return response?.existe || false;
    
  } catch (error) {
    return false;
  }
};

/**
 * Listar imágenes de una entidad
 * @param {string} tipoEntidad - Tipo de entidad ('producto')
 * @returns {Promise} Lista de imágenes
 */
export const listarImagenes = async (tipoEntidad = 'producto') => {
  try {
    const response = await apiFetch(`${IMAGEN_API}/listar/${tipoEntidad}`);
    
    const imagenes = response.success ? response.data : (Array.isArray(response) ? response : []);
    
    return {
      success: true,
      data: imagenes,
      count: imagenes.length,
      message: response.message || 'Imágenes listadas'
    };
    
  } catch (error) {
    console.error('❌ Error listando imágenes:', error);
    return { success: false, error: error.message, data: [], count: 0 };
  }
};

// ==================== IMPORTACIÓN/EXPORTACIÓN ====================

/**
 * Importar productos desde archivo
 * @param {Array} productos - Lista de productos a importar
 * @param {number} idUsuario - ID del usuario que importa
 * @returns {Promise} Resultado de la importación
 */
export const importarProductos = async (productos, idUsuario) => {
  try {
    if (!productos || !productos.length) throw new Error('No hay productos para importar');
    
    console.log(`📥 Importando ${productos.length} productos`);
    
    const response = await apiFetch(`${API_BASE}/importar`, {
      method: 'POST',
      body: JSON.stringify({
        productos: productos,
        idUsuario: idUsuario || null
      })
    });
    
    return {
      success: true,
      data: response.data || response,
      message: response.message || 'Importación completada',
      productosImportados: response.productosImportados || 0
    };
    
  } catch (error) {
    console.error('❌ Error importando productos:', error);
    return { success: false, error: error.message, data: null, productosImportados: 0 };
  }
};

/**
 * Exportar productos
 * @param {Object} criterios - Criterios de búsqueda para exportar
 * @returns {Promise<Blob>} Archivo exportado
 */
export const exportarProductos = async (criterios = {}) => {
  try {
    console.log('📤 Exportando productos con criterios:', criterios);
    
    const currentServer = getCurrentServer();
    const url = `${currentServer.baseUrl.replace(':8080', ':5090')}${API_BASE}/exportar`;
    
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(criterios)
    });
    
    if (!response.ok) {
      throw new Error('Error al exportar productos');
    }
    
    return await response.blob();
    
  } catch (error) {
    console.error('❌ Error exportando productos:', error);
    return null;
  }
};

// ==================== FUNCIONES AUXILIARES ====================

/**
 * Obtener URL completa de imagen
 * @param {string} imagen - Nombre o ruta de imagen
 * @returns {string|null} URL completa
 */
export const obtenerUrlImagen = (imagen) => {
  if (!imagen?.trim()) return null;
  
  if (imagen.startsWith('http')) {
    return imagen;
  }
  
  const apiBaseUrl = getCurrentApiUrl();
  const nombre = imagen.split('/').pop();
  
  if (imagen.startsWith('/imagenes/')) {
    return `${apiBaseUrl}${imagen}`;
  }
  
  if (imagen.startsWith('imagenes/')) {
    return `${apiBaseUrl}/${imagen}`;
  }
  
  return `${apiBaseUrl}/imagenes/producto/${nombre}`;
};

/**
 * Hook para usar imagen
 * @param {string} imagen - Nombre de imagen
 * @returns {Object} Objeto con src y función onError
 */
export const useImagen = (imagen) => {
  const url = obtenerUrlImagen(imagen);
  
  return {
    src: url,
    onError: (e) => {
      e.target.style.display = 'none';
    }
  };
};

/**
 * Formatear número con formato venezolano
 * @param {number|string} numero - Número a formatear
 * @param {number} decimales - Número de decimales
 * @returns {string} Número formateado
 */
export const formatearMonto = (numero, decimales = 2) => {
  if (numero === null || numero === undefined || numero === '' || isNaN(numero)) {
    return decimales === 2 ? '0,00' : '0';
  }
  
  const num = Number(numero);
  if (isNaN(num)) return decimales === 2 ? '0,00' : '0';
  
  return num.toLocaleString('es-VE', {
    minimumFractionDigits: decimales,
    maximumFractionDigits: decimales
  });
};

/**
 * Formatear fecha
 * @param {string} fechaISO - Fecha en formato ISO
 * @param {boolean} completo - Si incluir hora
 * @returns {string} Fecha formateada
 */
export const formatearFecha = (fechaISO, completo = true) => {
  if (!fechaISO) return 'N/A';
  
  try {
    const fecha = new Date(fechaISO);
    const opciones = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: 'America/Caracas'
    };
    
    if (completo) {
      opciones.hour = '2-digit';
      opciones.minute = '2-digit';
      opciones.hour12 = false;
    }
    
    return fecha.toLocaleDateString('es-VE', opciones);
  } catch (error) {
    return fechaISO;
  }
};

/**
 * Convertir estado a texto legible
 * @param {string} estado - Estado del producto
 * @returns {string} Estado formateado
 */
export const convertirEstadoProductoATexto = (estado) => {
  if (!estado) return 'Sin estado';
  const estadoLower = estado.toLowerCase();
  if (estadoLower === 'activo') return 'Activo';
  if (estadoLower === 'inactivo') return 'Inactivo';
  return estado;
};

/**
 * Obtener clase CSS para estado
 * @param {string} estado - Estado del producto
 * @returns {string} Clase CSS
 */
export const obtenerClaseEstado = (estado) => {
  const estadoLower = estado?.toLowerCase();
  switch (estadoLower) {
    case 'activo':
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    case 'inactivo':
      return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
  }
};

/**
 * Obtener clase CSS para nivel de stock
 * @param {number} stock - Stock actual
 * @param {number} stockMinimo - Stock mínimo
 * @returns {string} Clase CSS
 */
export const obtenerClaseStock = (stock, stockMinimo) => {
  const stockNum = Number(stock) || 0;
  const stockMinimoNum = Number(stockMinimo) || 0;
  
  if (stockNum <= 0) {
    return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
  } else if (stockMinimoNum > 0 && stockNum <= stockMinimoNum) {
    return 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200';
  } else {
    return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
  }
};

/**
 * Obtener texto para nivel de stock
 * @param {number} stock - Stock actual
 * @param {number} stockMinimo - Stock mínimo
 * @returns {string} Texto descriptivo
 */
export const obtenerTextoStock = (stock, stockMinimo) => {
  const stockNum = Number(stock) || 0;
  const stockMinimoNum = Number(stockMinimo) || 0;
  
  if (stockNum <= 0) {
    return 'Sin stock';
  } else if (stockMinimoNum > 0 && stockNum <= stockMinimoNum) {
    return 'Bajo stock';
  } else {
    return 'En stock';
  }
};

/**
 * Calcular diferencia de precio
 * @param {number} precioCompra - Precio de compra
 * @param {number} precioVenta - Precio de venta
 * @returns {number} Diferencia
 */
export const calcularDiferenciaPrecio = (precioCompra, precioVenta) => {
  return Number(precioVenta || 0) - Number(precioCompra || 0);
};

/**
 * Calcular porcentaje de ganancia
 * @param {number} precioCompra - Precio de compra
 * @param {number} precioVenta - Precio de venta
 * @returns {number} Porcentaje de ganancia
 */
export const calcularPorcentajeGanancia = (precioCompra, precioVenta) => {
  const compra = Number(precioCompra) || 0;
  const venta = Number(precioVenta) || 0;
  
  if (compra <= 0) return 0;
  const diferencia = venta - compra;
  return (diferencia / compra) * 100;
};

/**
 * Calcular precio en Bs usando tasa de venta
 * @param {number} precioUSD - Precio en USD
 * @param {number} tasaVenta - Tasa de venta (opcional)
 * @returns {Promise<number>} Precio en Bs
 */
export const calcularPrecioBs = async (precioUSD, tasaVenta = null) => {
  try {
    if (!precioUSD || precioUSD <= 0) return 0;
    
    let tasa = tasaVenta;
    if (!tasa) {
      const tasaResponse = await obtenerTasaVentaActual();
      tasa = tasaResponse.success ? tasaResponse.data?.precio_usd_bs : null;
    }
    
    if (!tasa) return 0;
    
    return precioUSD * tasa;
    
  } catch (error) {
    console.error('❌ Error calculando precio en Bs:', error);
    return 0;
  }
};

/**
 * Calcular precio en USD usando tasa de compra
 * @param {number} precioBs - Precio en Bs
 * @param {number} tasaCompra - Tasa de compra (opcional)
 * @returns {Promise<number>} Precio en USD
 */
export const calcularPrecioUsd = async (precioBs, tasaCompra = null) => {
  try {
    if (!precioBs || precioBs <= 0) return 0;
    
    let tasa = tasaCompra;
    if (!tasa) {
      const tasaResponse = await obtenerTasaCompraActual();
      tasa = tasaResponse.success ? tasaResponse.data?.precio_usdt_bs : null;
    }
    
    if (!tasa) return 0;
    
    return precioBs / tasa;
    
  } catch (error) {
    console.error('❌ Error calculando precio en USD:', error);
    return 0;
  }
};

/**
 * Buscar productos localmente
 * @param {Array} productos - Lista de productos
 * @param {string} termino - Término de búsqueda
 * @returns {Array} Productos filtrados
 */
export const buscarProductosLocal = (productos, termino) => {
  if (!termino?.trim() || !Array.isArray(productos)) return productos || [];
  
  const terminoLower = termino.toLowerCase().trim();
  return productos.filter(producto => 
    (producto.codigo?.toLowerCase().includes(terminoLower)) ||
    (producto.codigoBarras?.toLowerCase().includes(terminoLower)) ||  // <-- NUEVO
    (producto.nombre?.toLowerCase().includes(terminoLower)) ||
    (producto.descripcion?.toLowerCase().includes(terminoLower)) ||
    (producto.marca?.toLowerCase().includes(terminoLower)) ||
    (producto.modelo?.toLowerCase().includes(terminoLower)) ||
    (producto.nombreCategoria?.toLowerCase().includes(terminoLower)) ||
    (producto.nombreProveedor?.toLowerCase().includes(terminoLower))
  );
};

/**
 * Filtrar productos por estado
 * @param {Array} productos - Lista de productos
 * @param {string} estado - 'all', 'activos', 'inactivos'
 * @returns {Array} Productos filtrados
 */
export const filtrarProductosPorEstado = (productos, estado) => {
  if (!Array.isArray(productos)) return [];
  if (estado === 'all') return productos;
  
  const estadoLower = estado.toLowerCase();
  if (estadoLower === 'activos') {
    return productos.filter(p => p.estado?.toLowerCase() === 'activo');
  }
  if (estadoLower === 'inactivos') {
    return productos.filter(p => p.estado?.toLowerCase() === 'inactivo');
  }
  return productos;
};

/**
 * Ordenar productos por diferentes criterios
 * @param {Array} productos - Lista de productos
 * @param {string} criterio - Criterio de ordenamiento
 * @returns {Array} Productos ordenados
 */
export const ordenarProductos = (productos, criterio = 'nombre') => {
  const array = [...(Array.isArray(productos) ? productos : [])];
  
  const criterios = {
    nombre: (a, b) => (a.nombre || '').localeCompare(b.nombre || ''),
    nombre_desc: (a, b) => (b.nombre || '').localeCompare(a.nombre || ''),
    codigo: (a, b) => (a.codigo || '').localeCompare(b.codigo || ''),
    codigo_desc: (a, b) => (b.codigo || '').localeCompare(a.codigo || ''),
    codigoBarras: (a, b) => (a.codigoBarras || '').localeCompare(b.codigoBarras || ''),  // <-- NUEVO
    codigoBarras_desc: (a, b) => (b.codigoBarras || '').localeCompare(a.codigoBarras || ''),  // <-- NUEVO
    precioCompra_desc: (a, b) => Number(b.precio_compra_usdt || 0) - Number(a.precio_compra_usdt || 0),
    precioCompra: (a, b) => Number(a.precio_compra_usdt || 0) - Number(b.precio_compra_usdt || 0),
    precioVenta_desc: (a, b) => Number(b.precio_venta_usd || 0) - Number(a.precio_venta_usd || 0),
    precioVenta: (a, b) => Number(a.precio_venta_usd || 0) - Number(b.precio_venta_usd || 0),
    stock_desc: (a, b) => (b.stock || 0) - (a.stock || 0),
    stock: (a, b) => (a.stock || 0) - (b.stock || 0),
    fechaCreacion_desc: (a, b) => new Date(b.fecha_creacion || 0) - new Date(a.fecha_creacion || 0),
    fechaCreacion: (a, b) => new Date(a.fecha_creacion || 0) - new Date(b.fecha_creacion || 0)
  };
  
  return array.sort(criterios[criterio] || criterios.nombre);
};

/**
 * Obtener estadísticas de productos localmente
 * @param {Array} productos - Lista de productos
 * @returns {Object} Estadísticas
 */
export const obtenerEstadisticasProductosLocal = (productos) => {
  const array = Array.isArray(productos) ? productos : [];
  
  const activos = array.filter(p => p?.estado?.toLowerCase() === 'activo').length;
  const inactivos = array.filter(p => p?.estado?.toLowerCase() === 'inactivo').length;
  
  const valorInventario = array
    .filter(p => p?.estado?.toLowerCase() === 'activo' && p?.stock > 0)
    .reduce((sum, p) => sum + (Number(p.precio_compra_usdt || 0) * (p.stock || 0)), 0);
  
  const valorVentaPotencial = array
    .filter(p => p?.estado?.toLowerCase() === 'activo' && p?.stock > 0)
    .reduce((sum, p) => sum + (Number(p.precio_venta_usd || 0) * (p.stock || 0)), 0);
  
  const bajoStock = array.filter(p => 
    p?.stock_minimo && p?.stock && p.stock <= p.stock_minimo
  ).length;
  
  const sinStock = array.filter(p => 
    p?.stock !== null && p?.stock !== undefined && p.stock <= 0
  ).length;
  
  const productosValidos = array.filter(p => p.precio_compra_usdt && p.precio_venta_usd);
  const promedioCompra = productosValidos.length > 0 
    ? productosValidos.reduce((sum, p) => sum + Number(p.precio_compra_usdt), 0) / productosValidos.length
    : 0;
  
  const promedioVenta = productosValidos.length > 0 
    ? productosValidos.reduce((sum, p) => sum + Number(p.precio_venta_usd), 0) / productosValidos.length
    : 0;
  
  return {
    total: array.length,
    activos,
    inactivos,
    porcentajeActivos: array.length > 0 ? Math.round((activos / array.length) * 100) : 0,
    porcentajeInactivos: array.length > 0 ? Math.round((inactivos / array.length) * 100) : 0,
    bajoStock,
    sinStock,
    porcentajeBajoStock: array.length > 0 ? Math.round((bajoStock / array.length) * 100) : 0,
    promedioCompra: Number(promedioCompra.toFixed(2)),
    promedioVenta: Number(promedioVenta.toFixed(2)),
    margenPromedio: promedioCompra > 0 ? Number((((promedioVenta - promedioCompra) / promedioCompra) * 100).toFixed(2)) : 0,
    valorInventario: Number(valorInventario.toFixed(2)),
    valorVentaPotencial: Number(valorVentaPotencial.toFixed(2)),
    gananciaPotencial: Number((valorVentaPotencial - valorInventario).toFixed(2)),
    gananciaPorcentual: valorInventario > 0 ? Number(((valorVentaPotencial - valorInventario) / valorInventario * 100).toFixed(2)) : 0
  };
};

/**
 * Función auxiliar para mostrar mensajes de error
 * @param {Error} error - Error capturado
 * @param {string} operacion - Operación que falló
 * @returns {string} Mensaje de error
 */
export const obtenerMensajeError = (error, operacion = 'operación') => {
  const mensaje = error.message || 'Error desconocido';
  
  if (mensaje.includes('Ya existe un producto con el código')) {
    return 'El código del producto ya existe. Por favor, utiliza un código diferente.';
  }
  
  if (mensaje.includes('Ya existe un producto con el código de barras')) {
    return 'El código de barras ya está registrado. Por favor, utiliza otro.';
  }
  
  if (mensaje.includes('Failed to fetch') || mensaje.includes('Network Error')) {
    return 'Error de conexión con el servidor. Verifica tu conexión a internet.';
  }
  
  if (mensaje.includes('401') || mensaje.includes('Unauthorized')) {
    return 'No autorizado. Por favor, inicia sesión nuevamente.';
  }
  
  return `Error al ${operacion}: ${mensaje}`;
};

// ==================== FUNCIONES DE EXPORTACIÓN ====================

/**
 * Exportar productos a Excel
 * @param {Array} productos - Lista de productos
 * @param {string} filename - Nombre del archivo
 * @returns {Object} Resultado de la exportación
 */
export const exportarExcel = (productos, filename = 'productos') => {
  try {
    if (typeof window === 'undefined') {
      return { success: false, error: 'Exportación solo disponible en el navegador', filename: null };
    }
    
    if (!productos?.length) throw new Error('No hay productos para exportar');
    
    const XLSX = require('xlsx');
    
    const data = productos.map((producto, index) => ({
      '#': index + 1,
      'Código': producto.codigo || '',
      'Código Barras': producto.codigoBarras || '',  // <-- NUEVO
      'Nombre': producto.nombre || '',
      'Marca': producto.marca || '',
      'Modelo': producto.modelo || '',
      'Categoría': producto.nombreCategoria || '',
      'Proveedor': producto.nombreProveedor || '',
      'Precio Compra ($)': formatearMonto(producto.precio_compra_usdt, 2),
      'Precio Venta ($)': formatearMonto(producto.precio_venta_usd, 2),
      'Stock': producto.stock || 0,
      'Stock Mínimo': producto.stock_minimo || '',
      'Garantía (meses)': producto.garantia_meses || 0,
      'Estado': producto.estado || ''
    }));
    
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Productos');
    
    const fecha = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `${filename}_${fecha}.xlsx`);
    
    return {
      success: true,
      filename: `${filename}_${fecha}.xlsx`,
      message: `Archivo generado exitosamente`,
      count: productos.length
    };
    
  } catch (error) {
    console.error('❌ Error exportando a Excel:', error);
    return { success: false, error: error.message, filename: null };
  }
};

/**
 * Exportar a PDF
 * @param {Array} productos - Lista de productos
 * @param {string} titulo - Título del reporte
 * @returns {Object} Resultado de la exportación
 */
export const exportarPDF = async (productos, titulo = 'Reporte de Productos') => {
  try {
    if (typeof window === 'undefined') {
      return { success: false, error: 'Exportación solo disponible en el navegador', filename: null };
    }
    
    if (!productos?.length) throw new Error('No hay productos para exportar');
    
    const { jsPDF } = await import('jspdf');
    await import('jspdf-autotable');
    
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.width;
    
    doc.setFontSize(16);
    doc.text('SYSTEM JJ', pageWidth / 2, 15, { align: 'center' });
    doc.setFontSize(12);
    doc.text(titulo, pageWidth / 2, 22, { align: 'center' });
    
    const tableData = productos.map(producto => [
      producto.codigo || '',
      producto.codigoBarras || '',  // <-- NUEVO
      producto.nombre?.substring(0, 20) || '',
      formatearMonto(producto.precio_compra_usdt, 2),
      formatearMonto(producto.precio_venta_usd, 2),
      producto.stock || 0,
      producto.estado || ''
    ]);
    
    doc.autoTable({
      startY: 30,
      head: [['Código', 'Cód. Barras', 'Nombre', 'Compra ($)', 'Venta ($)', 'Stock', 'Estado']],  // <-- NUEVO
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [79, 70, 229] },
      styles: { fontSize: 8 }
    });
    
    const fecha = new Date().toISOString().split('T')[0];
    doc.save(`productos_${fecha}.pdf`);
    
    return {
      success: true,
      filename: `productos_${fecha}.pdf`,
      message: 'PDF generado exitosamente',
      count: productos.length
    };
    
  } catch (error) {
    console.error('❌ Error exportando PDF:', error);
    return { success: false, error: error.message, filename: null };
  }
};


// ==================== RECÁLCULO DE PRECIOS ====================

/**
 * Simular recálculo de precios por compra (solo con tasa USDT)
 * @param {Object} params - Parámetros para la simulación
 * @param {number} params.nuevaTasaUSDTToBS - Nueva tasa USDT a Bs
 * @param {number} params.idUsuario - ID del usuario que realiza la operación
 * @param {boolean} params.redondearPrecios - Si debe redondear precios
 * @param {number} params.idCategoria - ID de categoría para filtrar (0 para todas)
 * @param {number} params.idProveedor - ID de proveedor para filtrar (0 para todos)
 * @param {boolean} params.simular - Si es solo simulación o ejecución real
 * @returns {Promise} Resultado de la simulación
 */
export const simularRecalculoCompra = async (params) => {
  try {
    console.log('🧮 Simulando recálculo de precios (compra):', params);
    
    const response = await apiFetch('/api/Producto/simular-recalculo-compra', {
      method: 'POST',
      body: JSON.stringify({
        nuevaTasaUSDTToBS: params.nuevaTasaUSDTToBS || 0,
        idUsuario: params.idUsuario || 0,
        redondearPrecios: params.redondearPrecios !== false,
        idCategoria: params.idCategoria || 0,
        idProveedor: params.idProveedor || 0,
        simular: params.simular !== false
      })
    });
    
    return {
      success: true,
      data: response,
      message: response.mensaje || 'Simulación completada'
    };
    
  } catch (error) {
    console.error('❌ Error en simulación de recálculo:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Simular recálculo de todos los precios (con ambas tasas)
 * @param {Object} params - Parámetros para la simulación
 * @param {number} params.nuevaTasaUSDToBS - Nueva tasa USD a Bs
 * @param {number} params.nuevaTasaUSDTToBS - Nueva tasa USDT a Bs
 * @param {number} params.idUsuario - ID del usuario que realiza la operación
 * @param {boolean} params.redondearPrecios - Si debe redondear precios
 * @param {number} params.idCategoria - ID de categoría para filtrar (0 para todas)
 * @param {number} params.idProveedor - ID de proveedor para filtrar (0 para todos)
 * @param {boolean} params.simular - Si es solo simulación o ejecución real
 * @returns {Promise} Resultado de la simulación
 */
export const simularRecalculoTodos = async (params) => {
  try {
    console.log('🧮 Simulando recálculo de todos los precios:', params);
    
    const response = await apiFetch('/api/Producto/simular-recalculo-todos', {
      method: 'POST',
      body: JSON.stringify({
        nuevaTasaUSDToBS: params.nuevaTasaUSDToBS || 0,
        nuevaTasaUSDTToBS: params.nuevaTasaUSDTToBS || 0,
        idUsuario: params.idUsuario || 0,
        redondearPrecios: params.redondearPrecios !== false,
        idCategoria: params.idCategoria || 0,
        idProveedor: params.idProveedor || 0,
        simular: params.simular !== false
      })
    });
    
    return {
      success: true,
      data: response,
      message: response.mensaje || 'Simulación completada'
    };
    
  } catch (error) {
    console.error('❌ Error en simulación de recálculo total:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Ejecutar recálculo de precios (aplica los cambios)
 * @param {Object} params - Parámetros para el recálculo
 * @param {number} params.nuevaTasaUSDToBS - Nueva tasa USD a Bs
 * @param {number} params.nuevaTasaUSDTToBS - Nueva tasa USDT a Bs
 * @param {number} params.idUsuario - ID del usuario que realiza la operación
 * @param {boolean} params.redondearPrecios - Si debe redondear precios
 * @param {number} params.idCategoria - ID de categoría para filtrar (0 para todas)
 * @param {number} params.idProveedor - ID de proveedor para filtrar (0 para todos)
 * @returns {Promise} Resultado del recálculo
 */
export const ejecutarRecalculoPrecios = async (params) => {
  try {
    console.log('⚡ Ejecutando recálculo de precios:', params);
    
    const response = await simularRecalculoTodos({
      ...params,
      simular: false
    });
    
    return response;
    
  } catch (error) {
    console.error('❌ Error ejecutando recálculo:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Obtener resumen del recálculo de precios
 * @param {Object} resultado - Resultado de la simulación/ejecución
 * @returns {Object} Resumen formateado
 */
export const obtenerResumenRecalculo = (resultado) => {
  if (!resultado || !resultado.data) {
    return {
      productosProcesados: 0,
      productosActualizados: 0,
      productosConError: 0,
      resumen: 'Sin datos'
    };
  }
  
  const data = resultado.data;
  
  return {
    productosProcesados: data.productosProcesados || 0,
    productosActualizados: data.productosActualizados || 0,
    productosConError: data.productosConError || 0,
    tasaUSDToBS: data.tasaUSDToBS || 0,
    tasaUSDTToBS: data.tasaUSDTToBS || 0,
    fechaEjecucion: data.fechaEjecucion || new Date().toISOString(),
    esSimulacion: data.simular !== false,
    resumen: data.mensaje || (data.simular 
      ? `Simulación: ${data.productosActualizados || 0} productos actualizarían` 
      : `Ejecución: ${data.productosActualizados || 0} productos actualizados`)
  };
};

// ==================== FUNCIONES PARA EL MODAL DE RECÁLCULO ====================

/**
 * Validar parámetros de recálculo
 * @param {Object} params - Parámetros a validar
 * @returns {Object} Resultado de validación
 */
export const validarParametrosRecalculo = (params) => {
  const errores = [];
  
  if (!params.nuevaTasaUSDToBS || params.nuevaTasaUSDToBS <= 0) {
    errores.push('La tasa USD a Bs debe ser mayor a 0');
  }
  
  if (!params.nuevaTasaUSDTToBS || params.nuevaTasaUSDTToBS <= 0) {
    errores.push('La tasa USDT a Bs debe ser mayor a 0');
  }
  
  if (params.nuevaTasaUSDToBS && params.nuevaTasaUSDTToBS) {
    const diferencia = Math.abs(params.nuevaTasaUSDToBS - params.nuevaTasaUSDTToBS);
    const porcentajeDiferencia = (diferencia / params.nuevaTasaUSDToBS) * 100;
    
    if (porcentajeDiferencia > 20) {
      errores.push(`Las tasas tienen una diferencia del ${porcentajeDiferencia.toFixed(2)}%. ¿Estás seguro?`);
    }
  }
  
  return {
    valido: errores.length === 0,
    errores
  };
};

/**
 * Obtener tasas actuales para el recálculo
 * @returns {Promise<Object>} Tasas actuales
 */
export const obtenerTasasParaRecalculo = async () => {
  try {
    const [tasaCompraRes, tasaVentaRes] = await Promise.all([
      obtenerTasaCompraActual(),
      obtenerTasaVentaActual()
    ]);
    
    return {
      success: true,
      data: {
        tasaUSDToBS: tasaVentaRes.success ? tasaVentaRes.data?.precio_usd_bs : 0,
        tasaUSDTToBS: tasaCompraRes.success ? tasaCompraRes.data?.precio_usdt_bs : 0,
        fuenteUSD: tasaVentaRes.success ? tasaVentaRes.data?.fuente : 'Desconocida',
        fuenteUSDT: tasaCompraRes.success ? tasaCompraRes.data?.fuente : 'Desconocida',
        fechaUSD: tasaVentaRes.success ? tasaVentaRes.data?.fecha : null,
        fechaUSDT: tasaCompraRes.success ? tasaCompraRes.data?.fecha : null
      }
    };
    
  } catch (error) {
    console.error('❌ Error obteniendo tasas para recálculo:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

// ==================== EXPORT DEFAULT ====================

export default {
  // Funciones API principales
  obtenerProductos,
  obtenerProductoPorId,
  obtenerProductoPorCodigo,
  obtenerProductoPorCodigoBarras,  // <-- NUEVO
  buscarProductosAvanzado,
  crearProducto,
  actualizarProducto,
  eliminarProducto,
  cambiarEstadoProducto,
  ajustarStock,
  actualizarStock,
  incrementarStock,
  disminuirStock,
  
  // Consultas específicas
  obtenerProductosPorCategoria,
  obtenerProductosPorProveedor,
  obtenerProductosBajoStock,
  obtenerProductosSinStock,
  obtenerStockTotal,
  obtenerValorInventario,
  
  // Cálculo de precios
  calcularPrecioVenta,
  calcularPrecioPersonalizado,
  calcularPrecioRapido,
  actualizarPrecioProducto,
  actualizarPreciosEnLote,
  
  // Estadísticas y dashboard
  obtenerEstadisticasProducto,
  obtenerDashboardProductos,
  obtenerProductosMasVendidos,
  obtenerProductosMenosVendidos,
  
  // Historial y movimientos
  obtenerHistorialPrecios,
  obtenerMovimientosStock,
  
  // Validaciones
  verificarCodigo,
  verificarCodigoBarras,  // <-- NUEVO
  verificarProductoTieneMovimientos,
  
  // Funciones de imagen
  subirImagenProducto,
  obtenerUrlImagenDesdeApi,
  eliminarImagenProducto,
  validarImagen,
  descargarImagen,
  obtenerImagenBase64,
  verificarImagenExiste,
  listarImagenes,
  
  // Importación/Exportación
  importarProductos,
  exportarProductos,
  exportarExcel,
  exportarPDF,
  
  // Funciones auxiliares
  formatearMonto,
  formatearFecha,
  convertirEstadoProductoATexto,
  obtenerClaseEstado,
  obtenerClaseStock,
  obtenerTextoStock,
  calcularDiferenciaPrecio,
  calcularPorcentajeGanancia,
  calcularPrecioBs,
  calcularPrecioUsd,
  
  // Funciones de imagen auxiliares
  obtenerUrlImagen,
  useImagen,
  
  // Funciones de búsqueda y filtrado
  buscarProductosLocal,
  filtrarProductosPorEstado,
  obtenerEstadisticasProductosLocal,
  ordenarProductos,
  
  // Utilidades
  obtenerMensajeError,
  simularRecalculoCompra,
  simularRecalculoTodos,
  ejecutarRecalculoPrecios,
  obtenerResumenRecalculo,
  validarParametrosRecalculo,
  obtenerTasasParaRecalculo,
};